import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../hooks/useSEO';

const CONTACT_EMAIL = 'toolnova.hq@gmail.com';

const socialLinks = [
  {
    icon: 'ri-twitter-x-line',
    label: 'X (Twitter)',
    href: 'https://x.com/toolnovaHQ',
  },
  {
    icon: 'ri-instagram-line',
    label: 'Instagram',
    href: 'https://instagram.com/toolnova.ai',
  },
  {
    icon: 'ri-tiktok-line',
    label: 'TikTok',
    href: 'https://tiktok.com/@toolnova.ai',
  },
  {
    icon: 'ri-youtube-line',
    label: 'YouTube',
    href: 'https://youtube.com/@toolnovaHQ',
  },
];

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  useSEO({
    title: 'Contact ToolNova - Submit a Tool or Get in Touch',
    description: 'Have a question or want to submit an AI tool? Reach out to the ToolNova team — we respond within 48 hours.',
    keywords: 'contact toolnova, submit AI tool, get in touch, AI tool submission',
    canonical: `${siteUrl}/contact`,
    ogTitle: 'Contact ToolNova - Submit a Tool or Get in Touch',
    ogDescription: 'Have a question or want to submit an AI tool? Reach out to the ToolNova team — we respond within 48 hours.',
    ogType: 'website',
    ogUrl: `${siteUrl}/contact`,
    ogImage: 'https://public.readdy.ai/ai/img_res/2a35743cdb9600edc45f9b5e09018b91.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact ToolNova',
      description: 'Contact the ToolNova team for questions, tool submissions, partnerships, or general inquiries.',
      url: `${siteUrl}/contact`,
      mainEntity: {
        '@type': 'Organization',
        name: 'ToolNova',
        url: siteUrl,
        email: 'toolnova.hq@gmail.com',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'toolnova.hq@gmail.com',
          availableLanguage: 'English',
          contactOption: 'TollFree',
        },
      },
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.subject) newErrors.subject = 'Please select a subject.';
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required.';
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less.';
    }
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6p48smancq2eie827j0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setErrors({});
      } else {
        setSubmitError('Failed to send message. Please try again or email us directly.');
      }
    } catch {
      setSubmitError('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(45,212,191,0.08),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(6,182,212,0.06),transparent_50%)]"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <i className="ri-mail-line text-teal-400"></i>
            <span className="text-sm font-medium text-teal-300">Get In Touch</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Contact
            <span className="block bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Toolnova
            </span>
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Have questions, suggestions, or want to partner with us? We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Let&apos;s Connect</h2>
                <p className="text-gray-400 leading-relaxed mb-8">
                  Whether you have a question about our directory, want to submit a tool, or just want to say hello, our team is ready to answer all your questions.
                </p>
              </div>

              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <i className="ri-mail-line text-xl text-teal-400"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email Us</h3>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="text-gray-400 hover:text-teal-400 transition-colors duration-200 cursor-pointer break-all"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>

                {/* Response Time */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <i className="ri-time-line text-xl text-teal-400"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Response Time</h3>
                    <p className="text-gray-400">We typically respond within 24–48 hours</p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <i className="ri-share-line text-xl text-teal-400"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-3">Follow Us</h3>
                    <div className="flex flex-wrap gap-3">
                      {socialLinks.map((social) => (
                        <a
                          key={social.label}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          title={social.label}
                          className="w-10 h-10 flex items-center justify-center bg-gray-800/60 hover:bg-teal-500/20 border border-gray-700/60 hover:border-teal-500/60 rounded-lg text-gray-400 hover:text-teal-400 transition-all duration-300 cursor-pointer hover:scale-110"
                        >
                          <i className={`${social.icon} text-base`}></i>
                        </a>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      X &bull; Instagram &bull; TikTok &bull; YouTube
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/60 rounded-2xl p-8">
                {!isSubmitted ? (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    noValidate
                  >
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-gray-900/60 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-200 text-sm ${
                          errors.name
                            ? 'border-red-500/70 focus:ring-red-500/20'
                            : 'border-gray-700/60 focus:border-teal-500/60 focus:ring-teal-500/20'
                        }`}
                        placeholder="John Doe"
                      />
                      {errors.name && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i> {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-gray-900/60 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-200 text-sm ${
                          errors.email
                            ? 'border-red-500/70 focus:ring-red-500/20'
                            : 'border-gray-700/60 focus:border-teal-500/60 focus:ring-teal-500/20'
                        }`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i> {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Subject */}
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-gray-900/60 border rounded-lg text-white focus:outline-none focus:ring-2 transition-all duration-200 text-sm cursor-pointer ${
                          errors.subject
                            ? 'border-red-500/70 focus:ring-red-500/20'
                            : 'border-gray-700/60 focus:border-teal-500/60 focus:ring-teal-500/20'
                        }`}
                      >
                        <option value="">Select a subject</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Submit a Tool">Submit a Tool</option>
                        <option value="Partnership">Partnership Opportunity</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.subject && (
                        <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i> {errors.subject}
                        </p>
                      )}
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={6}
                        className={`w-full px-4 py-3 bg-gray-900/60 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all duration-200 resize-none text-sm ${
                          errors.message
                            ? 'border-red-500/70 focus:ring-red-500/20'
                            : 'border-gray-700/60 focus:border-teal-500/60 focus:ring-teal-500/20'
                        }`}
                        placeholder="Tell us how we can help you..."
                      ></textarea>
                      <div className="flex items-center justify-between mt-1">
                        {errors.message ? (
                          <p className="text-xs text-red-400 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i> {errors.message}
                          </p>
                        ) : (
                          <span />
                        )}
                        <p className={`text-xs ml-auto ${formData.message.length >= 500 ? 'text-red-400' : 'text-gray-500'}`}>
                          {formData.message.length}/500
                        </p>
                      </div>
                    </div>

                    {/* Submit Error */}
                    {submitError && (
                      <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        <i className="ri-error-warning-line flex-shrink-0"></i>
                        <span>{submitError}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-400 hover:to-cyan-400 transition-all duration-300 shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <span>Send Message</span>
                          <i className="ri-send-plane-line"></i>
                        </span>
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 flex items-center justify-center bg-teal-500/10 border border-teal-500/30 rounded-full mx-auto mb-6">
                      <i className="ri-check-line text-4xl text-teal-400"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Message Sent Successfully!</h3>
                    <p className="text-gray-400 mb-2">
                      Thank you for reaching out. We&apos;ll get back to you within 24–48 hours.
                    </p>
                    <p className="text-gray-500 text-sm mb-8">
                      You can also reach us directly at{' '}
                      <a href={`mailto:${CONTACT_EMAIL}`} className="text-teal-400 hover:text-teal-300 transition-colors">
                        {CONTACT_EMAIL}
                      </a>
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700/60 hover:bg-gray-700 text-white font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      <span>Send Another Message</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}